import { useContext, useEffect, useRef, useState } from "react";
import { getTourCarousels } from "./house-service";
import { Carousel, CarouselItems, CarouselMedia } from "./types";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/a11y";
import { HouseCompetitionData } from "./contextProvider";
import Skeleton from "react-loading-skeleton";
import { A11y, Navigation } from "swiper/modules";
import DOMPurify from "dompurify";
import { useIsMobile } from "@/app/redux/features/device/selectors";

const PhotoTour = () => {
  const [carousel, setCarousel] = useState<Carousel[]>();
  const [swiperData, setSwiperData] = useState<CarouselMedia[]>([]);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const swiperRef = useRef(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { prizeId } = useContext(HouseCompetitionData);

  useEffect(() => {
    if (prizeId) {
      getPhotoTourData();
    }
  }, [prizeId]);

  const getPhotoTourData = async () => {
    try {
      const response = await getTourCarousels(prizeId, 'v2');
      const { Items } = (await response.json()) as CarouselItems;
      setCarousel(Items);
      setSwiperData(Items[0].Media);
    } catch (err) {
      console.log(`Error while fetching the carousel data - ${JSON.stringify(err)}`);
    } finally {
      setIsLoading(false);
    }
  };

  const onTabClick = (item: Carousel, tabIndex: number) => {
    setSwiperData(item.Media);
    setActiveTabIndex(tabIndex);
    setActiveSlideIndex(0);
    // Reset Swiper slide index to 0
    if (swiperRef.current) {
      swiperRef.current.slideTo(0); // Resets Swiper to first slide
    }
  };

  const isMobile = useIsMobile();

  return (
    <div className="flex flex-col">
      {isLoading ? (
        <Skeleton inline highlightColor="#fff" height={500} containerClassName="w-full bg-[#f1f1f1]" />
      ) : (
        <>
          <div
            className={
              "flex max-lg:gap-2 max-lg:whitespace-nowrap max-lg:overflow-auto items-center lg:bg-[#F1F1F1] lg:rounded-t-2xl"
            }>
            {carousel?.map((item, index) => (
              <div
                key={index}
                className={`self-start font-jost lg:flex-1 text-sm lg:text-lg lg:text-center font-normal leading-5 lg:leading-6 p-2 max-lg:pt-0 lg:py-3 hover:text-primary cursor-pointer ${activeTabIndex === index ? "border-b border-solid border-b-primary text-primary" : "text-gray-1"}`}
                onClick={() => onTabClick(item, index)}>
                {item.Name}
              </div>
            ))}
          </div>
          <div className="w-full relative carousel-container-within-slide-navigation">
            <Swiper
              modules={[Navigation, A11y]}
              slidesPerView={1}
              loop={false}
              spaceBetween={0}
              navigation
              onSlideChangeTransitionEnd={swiper => {
                setActiveSlideIndex(swiper.realIndex);
              }}
              onSwiper={swiper => (swiperRef.current = swiper)}
              className={"w-full lg:rounded-b-2xl"}
              style={{
                zIndex: 0,
              }}>
              {swiperData.map((mediaItem, index) => (
                <SwiperSlide
                  key={index}
                  className={"max-lg:mt-5 max-lg:rounded-lg lg:rounded-b-2xl lg:relative"}
                  style={{ height: "auto" }}>
                  <div
                    className={"w-full h-full flex overflow-hidden relative max-lg:rounded-lg lg:rounded-b-2xl"}
                    style={{
                      boxShadow: "0px 1.5px 8px 0px rgba(0, 0, 0, 0.08)",
                    }}>
                    {!mediaItem.IsVideo ? (
                      <img src={mediaItem.Url} alt={`slide-${index}`} className="w-full" loading="lazy" />
                    ) : (
                      <video src={mediaItem.Url} className="h-full" autoPlay loop muted playsInline />
                    )}
                    {swiperData.length > 1 && isMobile && (
                      <span className="absolute lg:z-20 bottom-3 lg:bottom-10 left-3 lg:left-10 bg-black/50 rounded text-white text-[10px] lg:text-base leading-3 lg:leading-5.5 font-medium px-1.5 py-[3px]">
                        {activeSlideIndex + 1}/{swiperData.length}
                      </span>
                    )}
                  </div>
                </SwiperSlide>
              ))}
              {carousel?.[activeTabIndex]?.Description && (
                <div
                  className={
                    "lg:pb-10 lg:z-[1] lg:absolute lg:bottom-0 lg:inset-x-0 lg:bg-gradient-to-t lg:from-black lg:to-transparent lg:h-1/2 lg:flex lg:items-end lg:justify-center lg:rounded-b-2xl"
                  }>
                  <div
                    className={
                      "w-full max-lg:mt-3 text-gray-1 lg:text-white text-base lg:text-lg leading-5.5 lg:leading-6 font-normal lg:text-center lg:max-w-[1140px] lg:mx-auto"
                    }
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(carousel?.[activeTabIndex]?.Description),
                    }}
                  />
                </div>
              )}
              {swiperData.length > 1 && !isMobile && (
                <div className="absolute lg:z-20 bottom-3 lg:bottom-10 left-3 lg:left-10 bg-black/50 rounded text-white text-[10px] lg:text-base leading-3 lg:leading-5.5 font-medium px-1.5 py-[3px]">
                  {activeSlideIndex + 1}/{swiperData.length}
                </div>
              )}
            </Swiper>
          </div>
        </>
      )}
    </div>
  );
};

export default PhotoTour;
